import type { BiorhythmSeries } from "../../lib/biorhythm";

const GUIDE_LINE_POSITIONS = ["24.64%", "50%", "75.36%"] as const;
const GRID_LINE_OFFSETS = [0, 49, 98, 147, 196, 245, 286] as const;
const Y_AXIS_LABELS = ["100%", "50%", "0%", "-50%", "-100%"] as const;

const VIEWBOX_WIDTH = 1000;
const VIEWBOX_HEIGHT = 195;
const TODAY_POINT_RADIUS = 7;
const TODAY_POINT_BORDER_WIDTH = 3;
const TODAY_POINT_DIAMETER = TODAY_POINT_RADIUS * 2;

const CHART_COLORS = {
  intellectual: { active: "#4EFFE5", faded: "rgba(78, 255, 229, 0.24)" },
  emotional: { active: "#FF4A94", faded: "rgba(255, 74, 148, 0.24)" },
  physical: { active: "#8E63FF", faded: "rgba(142, 99, 255, 0.24)" },
} as const;

type LineKey = keyof typeof CHART_COLORS;

const toChartY = (value: number) => ((100 - value) / 200) * VIEWBOX_HEIGHT;

const toPolylinePoints = (values: number[]): string => {
  if (values.length <= 1) {
    return `0,${toChartY(values[0] ?? 0)}`;
  }

  return values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * VIEWBOX_WIDTH;
      const y = toChartY(value);
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");
};

interface BiorhythmChartProps {
  series: BiorhythmSeries | null;
}

/** Three biorhythm curves around today (legacy Today/Biorhythm chart geometry). */
export function BiorhythmChart({ series }: BiorhythmChartProps) {
  if (!series) {
    return null;
  }

  const pointsCount = series.physical.length;
  const todayPercent = pointsCount > 1 ? (series.todayIndex / (pointsCount - 1)) * 100 : 50;

  const physicalTodayY = toChartY(series.physical[series.todayIndex] ?? 0);
  const emotionalTodayY = toChartY(series.emotional[series.todayIndex] ?? 0);
  const intellectualTodayY = toChartY(
    series.intellectual[series.todayIndex] ?? 0,
  );

  return (
    <div className="relative h-[338px] w-full select-none font-body">
      <div className="absolute left-0 right-0 top-12 h-[286px]">
        {GRID_LINE_OFFSETS.map((lineOffset) => (
          <div
            key={lineOffset}
            className="absolute left-0 right-0 h-px bg-purple-light/10"
            style={{ top: `${lineOffset}px` }}
          />
        ))}

        {Y_AXIS_LABELS.map((label, index) => (
          <div
            key={label}
            className="absolute left-4 text-[10px] font-medium leading-[10px] text-purple-light/40"
            style={{ top: `${5 + index * 49}px` }}
          >
            {label}
          </div>
        ))}
      </div>

      <div className="absolute left-0 right-0 top-0 h-[302px]">
        <div
          className="absolute bottom-0 top-0 z-[2] w-0.5 bg-[#F5EBFF]"
          style={{
            left: GUIDE_LINE_POSITIONS[1],
            transform: "translateX(-1px)",
          }}
        />
        <div
          className="absolute top-0 z-[1] h-[293px] w-px bg-purple-light/20"
          style={{
            left: GUIDE_LINE_POSITIONS[0],
            transform: "translateX(-0.5px)",
          }}
        />
        <div
          className="absolute top-0 z-[1] h-[293px] w-px bg-purple-light/20"
          style={{
            left: GUIDE_LINE_POSITIONS[2],
            transform: "translateX(-0.5px)",
          }}
        />
      </div>

      <div className="absolute left-0 right-0 top-[49px] z-[3] h-[195px]">
        <svg
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          preserveAspectRatio="none"
          className="h-full w-full"
          aria-hidden
        >
          <defs>
            {(Object.keys(CHART_COLORS) as LineKey[]).map((key) => (
              <linearGradient
                key={key}
                id={`bio-${key}`}
                x1="0"
                y1="0"
                x2="1"
                y2="0"
              >
                <stop offset="0%" stopColor={CHART_COLORS[key].faded} />
                <stop offset="50%" stopColor={CHART_COLORS[key].active} />
                <stop offset="100%" stopColor={CHART_COLORS[key].faded} />
              </linearGradient>
            ))}
          </defs>

          <polyline
            points={toPolylinePoints(series.physical)}
            fill="none"
            stroke="url(#bio-physical)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={toPolylinePoints(series.emotional)}
            fill="none"
            stroke="url(#bio-emotional)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
          <polyline
            points={toPolylinePoints(series.intellectual)}
            fill="none"
            stroke="url(#bio-intellectual)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* `preserveAspectRatio="none"` stretches SVG non-uniformly, so native
            `<circle>` elements become ovals. Markers are drawn in CSS pixels. */}
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-dark-base"
          style={{
            left: `${todayPercent}%`,
            top: `${physicalTodayY}px`,
            width: `${TODAY_POINT_DIAMETER}px`,
            height: `${TODAY_POINT_DIAMETER}px`,
            borderWidth: `${TODAY_POINT_BORDER_WIDTH}px`,
            backgroundColor: CHART_COLORS.physical.active,
          }}
        />
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-dark-base"
          style={{
            left: `${todayPercent}%`,
            top: `${emotionalTodayY}px`,
            width: `${TODAY_POINT_DIAMETER}px`,
            height: `${TODAY_POINT_DIAMETER}px`,
            borderWidth: `${TODAY_POINT_BORDER_WIDTH}px`,
            backgroundColor: CHART_COLORS.emotional.active,
          }}
        />
        <span
          className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-dark-base"
          style={{
            left: `${todayPercent}%`,
            top: `${intellectualTodayY}px`,
            width: `${TODAY_POINT_DIAMETER}px`,
            height: `${TODAY_POINT_DIAMETER}px`,
            borderWidth: `${TODAY_POINT_BORDER_WIDTH}px`,
            backgroundColor: CHART_COLORS.intellectual.active,
          }}
        />
      </div>

      <div
        className="absolute top-[302px] w-[95px] -translate-x-1/2 text-center font-body text-xs font-normal leading-6 text-purple-light/40"
        style={{ left: GUIDE_LINE_POSITIONS[0] }}
      >
        {series.labels.left}
      </div>

      <div
        className="absolute top-[302px] z-[4] -translate-x-1/2 rounded-xl bg-purple-light px-2 py-1 text-center font-body text-xs font-medium leading-3 text-dark-base"
        style={{ left: GUIDE_LINE_POSITIONS[1] }}
      >
        {series.labels.center}
      </div>

      <div
        className="absolute top-[302px] w-[95px] -translate-x-1/2 text-center font-body text-xs font-normal leading-6 text-purple-light/40"
        style={{ left: GUIDE_LINE_POSITIONS[2] }}
      >
        {series.labels.right}
      </div>
    </div>
  );
}
